import { forEach, set, isEqual } from 'lodash'
import Vue from 'vue'
import { customEncode } from '../helper/helper'
import Vuex from 'vuex'
Vue.use(Vuex)

// JSON của các giá trị object lúc setPersonalized commit lần gần nhất, theo
// artworkID → key (xem setPersonalized). Để ngoài state cho khỏi reactive.
const committedSnapshots = {}
// null = không chụp được (vd tham chiếu vòng) → coi như đã đổi, như code cũ.
// File/Blob stringify ra {} nên ghi thêm tên/kích thước/thời điểm để phân biệt.
const snapshotOf = value => {
  try {
    return JSON.stringify(value, (k, v) =>
      typeof Blob !== 'undefined' && v instanceof Blob
        ? `blob:${v.size}:${v.type}:${v.name || ''}:${v.lastModified || ''}`
        : v
    )
  } catch (err) {
    return null
  }
}

const store = new Vuex.Store({
  state: {
    platform: null,
    product: null,
    variants: [],
    artworks: [],
    mockups: [],
    settings: {},
    printAreas: [],
    variantMockups: [],
    selectedArtworkID: null,
    personalized: {},
    ratio: 1,
    ratios: {},
    addToCartText: 'Add to cart',
    selectedVariant: null,
    artworkFocus: false,
    artworkPreviews: {},
    currentSlide: 0,
    loadings: {},
    mockupLoadings: {},
    imageLoadings: {},
    bundledProducts: [],
    siteURL: null,
    cart_data: {},
    personalizedText: [],
    loadingMedia: false,
    messageError: false,
    loadingForm: false,
    isSaveLocalstorage: false
  },
  getters: {
    platform: state => state.platform,
    product: state => state.product,
    variants: state => state.variants,
    artworks: state => state.artworks,
    mockups: state => state.mockups,
    settings: state => state.settings,
    printAreas: state => state.printAreas,
    variantMockups: state => state.variantMockups,
    personalized: state => state.personalized,
    ratio: state => state.ratio,
    ratios: state => state.ratios,
    addToCartText: state => state.addToCartText,
    artworkFocus: state => state.artworkFocus,
    selectedVariant: state => state.selectedVariant,
    selectedArtworkID: state => state.selectedArtworkID,
    artworkPreviews: state => state.artworkPreviews,
    currentSlide: state => state.currentSlide,
    loadings: state => state.loadings,
    mockupLoadings: state => state.mockupLoadings,
    imageLoadings: state => state.imageLoadings,
    bundledProducts: state => state.bundledProducts,
    siteURL: state => state.state,
    getCartData: state => state.cart_data,
    getPersonalizedText: state => state.personalizedText,
    loadingMedia: state => state.loadingMedia,
    messageError: state => state.messageError,
    loadingForm: state => state.loadingForm,
    isSaveLocalstorage: state => state.isSaveLocalstorage
  },
  mutations: {
    setPlatform(state, platform) {
      state.platform = platform
    },
    setProduct(state, product) {
      state.product = product
    },
    setVariants(state, variants) {
      state.variants = variants
    },
    setArtworks(state, artworks) {
      state.artworks = artworks
    },
    setMockups(state, mockups) {
      state.mockups = mockups
    },
    setSettings(state, settings) {
      state.settings = settings
    },
    setPrintAreas(state, printAreas) {
      state.printAreas = printAreas
    },
    setVariantMockups(state, variantMockups) {
      state.variantMockups = variantMockups
    },
    setRatio(state, ratio) {
      state.ratio = ratio
    },
    setRatios(state, { artworkID, ratio }) {
      state.ratios[artworkID] = ratio
    },
    setPersonalized(state, { artworkID, data }) {
      const {isSaveLocalstorage, product} = this.getters;
      const cleanedData = {}
      Object.keys(data || {}).forEach(key => {
        if (data[key] !== undefined) {
          cleanedData[key] = data[key]
        }
      })
      // Bỏ qua khi không có gì đổi. Lúc dựng form, mỗi ô input phát `input`
      // với đúng giá trị đang có (đo trên sản phẩm 17 layer: 111/139 lần), mà
      // thay cả object `personalized` thì mọi checkCondition, layer artwork và
      // watcher deep đều chạy lại. Giá trị object thường được gửi lại cùng
      // tham chiếu, và vài layer (cut face, remove bg, handwriting) sửa nó tại
      // chỗ (`context.model[x] = …`), nên so với bản chụp lúc commit trước.
      // Gọi không kèm dữ liệu (addToCart trong Form.vue sửa object tại chỗ rồi
      // commit rỗng để ép vẽ lại trước khi chụp preview) thì vẫn chạy như cũ.
      const current = state.personalized[artworkID]
      const snapshots = committedSnapshots[artworkID] || {}
      const keys = Object.keys(cleanedData)
      if (current && keys.length > 0 && keys.every(key => {
        const a = current[key]
        const b = cleanedData[key]
        if (a === b) {
          if (a === null || typeof a !== 'object') return true
          const snap = snapshotOf(b)
          return snap !== null && snapshots[key] === snap
        }
        return isEqual(a, b)
      })) {
        return
      }
      Object.keys(cleanedData).forEach(key => {
        const value = cleanedData[key]
        const snap = value !== null && typeof value === 'object' ? snapshotOf(value) : null
        if (snap !== null) {
          snapshots[key] = snap
        } else {
          delete snapshots[key]
        }
      })
      committedSnapshots[artworkID] = snapshots
      state.personalized = {
        ...state.personalized,
        [artworkID]: {
          ...(state.personalized[artworkID] || { template: 0 }),
          ...cleanedData
        }
      }
      if(isSaveLocalstorage && product.id){
        const product_id = product.id;
        let saved = JSON.stringify(state.personalized);
        saved = customEncode(saved, product_id.toString())
        localStorage.setItem(`store-layers-${product_id}`, saved);
      }
    },
    setPersonalizedText(state, data){
      state.personalizedText = data;
    },
    setAddToCartText(state, text) {
      state.addToCartText = text
    },
    setArtworkFocus(state, focus) {
      state.artworkFocus = focus
    },
    setSelectedVariant(state, variant) {
      state.selectedVariant = variant
    },
    setSelectedArtworkID(state, artworkID) {
      state.selectedArtworkID = artworkID
    },
    setArtworkPreviews(state, { artworkID, data }) {
      state.artworkPreviews = { ...state.artworkPreviews, [artworkID]: data }
    },
    setCurrentSlide(state, index) {
      state.currentSlide = index
    },
    setLoadings(state, { artworkID, key, loading }) {
      state.loadings = {
        ...state.loadings,
        [artworkID]: {
          ...(state.loadings[artworkID] || {}),
          [key]: loading
        }
      }
    },
    setMockupLoadings(state, { mockupID, key, loading }) {
      state.mockupLoadings = {
        ...state.mockupLoadings,
        [mockupID]: {
          ...(state.mockupLoadings[mockupID] || {}),
          [key]: loading
        }
      }
    },
    setImageLoadings(state, { key, loading }) {
      state.imageLoadings = {
        ...state.imageLoadings,
        [key]: loading
      }
    },
    setBundledProducts(state, bundledProducts){
      state.bundledProducts = bundledProducts
    },
    setSiteURL(state, siteURL){
      state.siteURL = siteURL
    },
    setCartDataByKey(state, payload){
      const { product } = this.getters;
      state.cart_data[payload.key] = {
        'name': payload.name,
        'value': payload.value
      };
      if(product.id){
        const product_id = product.id;
        let data = JSON.stringify(state.cart_data);
        data = customEncode(data, product_id.toString());
        localStorage.setItem(`cart-data-root-${product_id}`, data);
      }
    },
    setCartData(state, payload){
      state.cart_data = payload;
    },
    setLoadingMedia(state, status){
      state.loadingMedia = status;
    },
    setMessageError(state, status){
      state.messageError = status;
    },
    setLoadingForm(state, status){
      state.loadingForm = status;
    },
    setIsSaveLocalstorage(state, status){
      state.isSaveLocalstorage = status;
    }
  },
  actions: {
    initPersonalized({ commit }, data) {
      forEach(data, (data, artworkID) => {
        commit('setPersonalized', { artworkID, data })
      })
    },
    setPersonalized({ commit }, { artworkID, data }) {
      commit('setPersonalized', { artworkID, data })
    },
    setPlatform({ commit }, platform) {
      commit('setPlatform', platform)
    },
    setProduct({ commit }, product) {
      commit('setProduct', product)
    },
    setVariants({ commit }, variants) {
      commit('setVariants', variants)
    },
    setArtworks({ commit }, artworks) {
      commit('setArtworks', artworks)
    },
    setSettings({ commit }, settings) {
      commit('setSettings', settings)
    },
    setMockups({ commit }, mockups) {
      commit('setMockups', mockups)
    },
    setVariantMockups({ commit }, variantMockups) {
      commit('setVariantMockups', variantMockups)
    },
    setPrintAreas({ commit }, printAreas) {
      commit('setPrintAreas', printAreas)
    },
    setRatios({ commit }, ratios) {
      commit('setRatios', ratios)
    },
    setBundledProducts({commit}, bundledProducts){
      commit('setBundledProducts', bundledProducts)
    },
    setSiteURL({commit}, siteURL){
      commit('setSiteURL', siteURL)
    }
  }
})

export default store



// WEBPACK FOOTER //
// ./src/store/index.js